/**
 * Roles List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, StatsCard } from '../../components/common';
import { useGetRolesQuery, useDeleteRoleMutation } from '../../store/api/rbacApi';
import { Role } from '../../types/rbac';
import { palette } from '../../theme/palette';
import toast from 'react-hot-toast';

const RolesListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const { data, isLoading } = useGetRolesQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
  });

  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  const handleDelete = async () => {
    if (!selectedRole) return;
    try {
      await deleteRole(selectedRole.id).unwrap();
      toast.success(t('rbac.roles.toast.deleteSuccess', { name: selectedRole.name }));
      setDeleteDialogOpen(false);
      setSelectedRole(null);
    } catch (error: any) {
      console.error('Failed to delete role:', error);
      toast.error(error?.data?.message || t('rbac.roles.toast.deleteError'));
    }
  };

  const columns: Column<Role>[] = [
    {
      id: 'name',
      label: t('rbac.roles.cols.name'),
      minWidth: 200,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Box>
            <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
              {row.name}
            </Box>
            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {row.code}
            </Box>
          </Box>
          {row.is_system && (
            <Chip
              label={t('rbac.roles.chip.system')}
              size="small"
              icon={<LockIcon />}
              sx={{
                backgroundColor: palette.brand[50],
                color: palette.brand[700],
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'description',
      label: t('rbac.roles.cols.description'),
      minWidth: 250,
      render: (row) => (
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          {row.description || '-'}
        </Box>
      ),
    },
    {
      id: 'permission_count',
      label: t('rbac.roles.cols.permissions'),
      minWidth: 100,
      align: 'center',
      render: (row) => (
        <Chip
          label={row.permission_count}
          size="small"
          sx={{
            backgroundColor: palette.brandSecondary[50],
            color: palette.brandSecondary[700],
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      id: 'employee_count',
      label: t('rbac.roles.cols.employees'),
      minWidth: 100,
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <span>{row.employee_count}</span>
        </Box>
      ),
    },
    {
      id: 'is_active',
      label: t('rbac.roles.cols.status'),
      minWidth: 100,
      align: 'center',
      render: (row) => (
        <Chip
          label={row.is_active ? t('rbac.status.active') : t('rbac.status.inactive')}
          size="small"
          sx={{
            backgroundColor: row.is_active
              ? palette.status.success + '20'
              : '#E2E8F0',
            color: row.is_active
              ? palette.status.success
              : '#64748B',
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      id: 'actions',
      label: t('common.actions'),
      minWidth: 120,
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title={t('rbac.roles.tooltip.view')}>
            <IconButton
              size="small"
              onClick={() => navigate(`/roles/${row.id}`)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!row.is_system && (
            <>
              <Tooltip title={t('rbac.roles.tooltip.edit')}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => navigate(`/roles/${row.id}/edit`)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('rbac.roles.tooltip.delete')}>
                <IconButton
                  size="small"
                  color="error"
                  disabled={row.employee_count > 0}
                  onClick={() => {
                    setSelectedRole(row);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  const stats = data?.results
    ? [
        {
          title: t('rbac.roles.stats.total'),
          value: data.count.toString(),
          icon: <SecurityIcon />,
          color: palette.brand[500],
        },
        {
          title: t('rbac.roles.stats.active'),
          value: data.results.filter((r) => r.is_active).length.toString(),
          icon: <SecurityIcon />,
          color: palette.status.success,
        },
        {
          title: t('rbac.roles.stats.system'),
          value: data.results.filter((r) => r.is_system).length.toString(),
          icon: <LockIcon />,
          color: palette.brand[700],
        },
        {
          title: t('rbac.roles.stats.custom'),
          value: data.results.filter((r) => !r.is_system).length.toString(),
          icon: <SecurityIcon />,
          color: palette.brandSecondary[500],
        },
      ]
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('rbac.roles.title')}
        subtitle={t('rbac.roles.subtitle')}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/roles/create')}
          >
            {t('rbac.roles.createButton')}
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 3 }}>
        {stats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('rbac.roles.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300, flexGrow: 1 }}
          />
        </Box>
      </Card>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('rbac.roles.dialog.deleteTitle')}</DialogTitle>
        <DialogContent>
          {selectedRole && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('rbac.roles.dialog.deleteConfirm', { name: selectedRole.name })}
              </Alert>
              {selectedRole.employee_count > 0 && (
                <Alert severity="error">
                  {t('rbac.roles.dialog.hasEmployees', { count: selectedRole.employee_count })}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting || (selectedRole?.employee_count || 0) > 0}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesListPage;

